import React from 'react'
import { Link } from 'react-router-dom'
import { useIsWithLaurel } from '../../hooks/useIsWithLaurel'
import { withTranslation } from 'react-i18next'
import { convertTime } from '@eventops/athlinks-lib-units'
import { verifyGender } from '../../utils/ERPUtil'
import { getTokenRacerId, isLoggedIn } from '../../utils/isLoggedIn'
import { getResultsUrlForBib } from '../../utils/resultsHelpers'
import toCapitalCase from '../../utils/toCapitalCase'
import { styles } from './styles'
import { configs } from '../../configs'
import { AthlinksClaimedFlame } from '../shared/AthlinksClaimedFlame'
import { RowArrow } from '../shared/RowArrow'
import { VRBadgeLink } from '../shared/VRBadge'
import { AzpRanking } from '../../types.results.reignite'
import { IntervalDivisionMetadata } from '../../utils/types'

type IntervalResultsRowMobileProps = {
  row: any
  index: number
  eventId: number
  eventCourseId: number
  masterId: number
  divisionId: number
  courseMetadata: IntervalDivisionMetadata
  isVirtual: boolean
  bracketName: string
  t: (key: string) => string
}

const IntervalResultsRowMobile: React.FC<IntervalResultsRowMobileProps> = ({
  row,
  index,
  eventId,
  eventCourseId,
  masterId,
  divisionId,
  courseMetadata,
  isVirtual,
  t,
}) => {
  const {
    bib,
    entryId,
    entryStatus,
    age,
    gender,
    displayName,
    time,
    racerId,
    locality,
    thirdPartyEntryId,
  } = row

  const isWithLaurel = useIsWithLaurel({
    eventCourseId,
    athlinksEventId: eventId,
    thirdPartyEntryId,
    isLive: true,
  })

  const getDivisionRank = (
    divisionId: number,
    divisionRanks: {
      genderRank: number
      primaryBracketRank: number
      overallRank: number
      otherRankings: AzpRanking[]
    }
  ) => {
    const divisionMetadata = courseMetadata?.divisions?.find(x => x.id === divisionId)

    let rank
    if (divisionMetadata) {
      switch (divisionMetadata.name) {
        case 'Male':
        case 'Female':
        case 'Non-Binary':
          rank = divisionRanks.genderRank
          break
        case 'Overall':
          rank = divisionRanks.overallRank
          break
        default:
          rank = divisionRanks.otherRankings?.find(x => x.id === divisionId)?.rank || divisionRanks.primaryBracketRank
          break
      }
    } else {
      rank = divisionRanks.overallRank
    }

    return rank === 999999 ? '--' : rank
  }

  const showGender = row.entryType?.toLowerCase() !== 'team'
  const irpUrlBib = getResultsUrlForBib(
    eventId,
    eventCourseId,
    bib,
    masterId,
    entryId
  )
  const racerName = (
    <span style={styles.mobile.name}>
      {toCapitalCase(displayName) || 'Unknown Racer'}
    </span>
  )

  const hasNonValidStatus = ['DNS', 'DNF', 'DQ'].includes(entryStatus)
  const timeToShow = convertTime({
    timeInMillis: time.timeInMillis,
    // @ts-ignore
    timeUnit: 'h',
  }).value
  const bibText = bib || ''
  const showSeparator = bib && (showGender || age || locality)
  const ageGenderLocationText = showGender
    ? `${verifyGender(age, gender, t)} ${age || ''}`
    : locality || ''
  const bracketRank = getDivisionRank(divisionId, row)

  const tokenRacerId = getTokenRacerId()
  const claimed = !!racerId

  //  @ts-ignore
  let actionOrTimeLabel: ReactElement = timeToShow
  if (isVirtual) {
    if (
      claimed &&
      isLoggedIn() &&
      racerId === tokenRacerId &&
      timeToShow === '0:00'
    ) {
      actionOrTimeLabel = (
        <span style={{ fontFamily: 'LatoBold', fontSize: 13 }}>
          {t('Post Time')}
        </span>
      )
    } else if (
      claimed &&
      isLoggedIn() &&
      racerId !== tokenRacerId &&
      timeToShow === '0:00'
    ) {
      actionOrTimeLabel = (
        <span style={{ fontFamily: 'LatoBold', fontSize: 13 }}>--</span>
      )
    } else if (!claimed && timeToShow === '0:00') {
      actionOrTimeLabel = (
        <span style={{ fontFamily: 'LatoBold', fontSize: 13 }}>--</span>
      )
    } else if (!isLoggedIn() && timeToShow === '0:00') {
      actionOrTimeLabel = (
        <span style={{ fontFamily: 'LatoBold', fontSize: 13 }}>
          {t('Post Time')}
        </span>
      )
    }
  }

  return (
    <div key={`result-row-${index}`}>
      <Link
        to={irpUrlBib}
        style={styles.tableRow(true, claimed) as React.CSSProperties}
        key={index}
      >
        <div className='row mx-0' style={styles.mobile.userRow}>
          <div
            className='col-sm-2 col-2'
            style={styles.mobile.rowIndex as React.CSSProperties}
          >
            {bracketRank}
          </div>
          <div className='col-sm-6 col-6 p-0'>
            <div style={{ paddingLeft: '6px' }}>
              <span>{racerName}</span>
              <br />
              {isWithLaurel && (
                <img
                  src={`${configs.cdnUrl}/svg/laurel_icon_small.svg`}
                  alt='Laurel Wreath Left Side'
                  style={{ marginRight: '10px' }}
                />
              )}

              <span style={styles.mobile.age}>{`${bibText}`}</span>
              {showSeparator ? (
                <span style={styles.mobile.separator}>{'|'}</span>
              ) : (
                ''
              )}
              <span style={styles.mobile.age}>
                {`${ageGenderLocationText}`}
              </span>
            </div>
          </div>
          <div className='col-sm-3 col-2 p-0'>
            <div style={styles.mobile.time as React.CSSProperties}>
              {hasNonValidStatus ? entryStatus : actionOrTimeLabel}
              {
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  {isVirtual === true ? (
                    <VRBadgeLink hideToolTip={true} />
                  ) : null}
                </div>
              }
              {racerId ? (
                <AthlinksClaimedFlame
                  width='14px'
                  height='14px'
                  // @ts-ignore
                  style={styles.claimFlame.mobile}
                />
              ) : null}
            </div>
          </div>
          <div className='col-sm-1 col-1' style={styles.rowArrowColumn.mobile}>
            <RowArrow />
          </div>
        </div>
      </Link>
    </div>
  )
}

export default withTranslation()(IntervalResultsRowMobile)
